import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Avatar, Button, Dropdown, Grid, Icon, Link, Text } from "~/components-shared";
import { useDispatch, useSelector } from "react-redux";
import requestCommentSelectors from "~/store/requestComments/selectors";
import { AuthoritiesEnum, CRUDActionEnum, RoutesEnum, StatusStateEnum } from "~/types/enums";
import { useHistory } from "react-router-dom";
import moment from "moment";
import requestCommentsActionCreators from "~/store/requestComments/actions";
import authSelectors from "~/store/auth/selectors";
import Markdown from "~/components/Markdown";
import useAbacExt from "~/hooks/abac";
import InputMarkdownTextArea from "~/components-shared/InputMarkdownTextArea";
import styled, { useTheme } from "styled-components";
import Picture from "../../components-shared/Picture";
const Actions = styled.div`
  display: flex;
  justify-content: end;
`;
const RequestComment = _ref => {
  let {
    requestId,
    comment
  } = _ref;
  const {
    colors
  } = useTheme();
  const {
    t
  } = useTranslation();
  const dispatch = useDispatch();
  const status = useSelector(requestCommentSelectors.getStatusByRequest(requestId));
  const createStatus = useSelector(requestCommentSelectors.getStatusByRequestComment("+"));
  const user = useSelector(authSelectors.getUser);
  const history = useHistory();
  const {
    userHasOneOf
  } = useAbacExt();
  const [updateId, setUpdateId] = useState("");
  const [updateText, setUpdateText] = useState("");
  useEffect(() => {
    if (status === StatusStateEnum.IDLE) {
      dispatch(requestCommentsActionCreators.fetchRequestStarted(requestId));
    }
  }, [dispatch, requestId, status]);
  const handleSubmitUpdate = () => {
    if (updateId && updateText && updateText !== "") {
      dispatch(requestCommentsActionCreators.updateStarted(requestId, updateId, {
        requestId,
        text: updateText
      }, data => {
        if (data) {
          setUpdateText("");
          setUpdateId(undefined);
        }
      }));
    }
  };
  const handleMember = id => {
    history.push(RoutesEnum.MEMBER.replace(":memberId", id));
  };
  const handleUpdate = (id, text) => {
    setUpdateId(id);
    setUpdateText(text);
  };
  const handleCancelUpdate = () => {
    setUpdateId(undefined);
    setUpdateText("");
  };
  const handleDelete = id => {
    dispatch(requestCommentsActionCreators.actionChanged(CRUDActionEnum.DELETE, id));
  };
  const handleLike = id => {
    dispatch(requestCommentsActionCreators.liked(id));
  };
  const handleUnlike = id => {
    dispatch(requestCommentsActionCreators.unliked(id));
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Avatar, {
    size: 12.5,
    user: comment.createdBy,
    showStatus: true,
    showEmoji: true
  }), /*#__PURE__*/React.createElement(Grid.Cell, null, user?.id === comment.createdBy.id ? /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold"
  }, comment.createdBy.firstName, " ", comment.createdBy.lastName) : /*#__PURE__*/React.createElement(Link, {
    weight: "semiBold",
    onClick: () => handleMember(comment.createdBy.id)
  }, comment.createdBy.firstName, " ", comment.createdBy.lastName), /*#__PURE__*/React.createElement(Text, {
    color: "textSecondary",
    s: {
      ml: 2
    }
  }, moment(comment.createdAt).local().startOf("seconds").fromNow(true), " ", t("Activities.ago")), comment.image && /*#__PURE__*/React.createElement(Picture, {
    src: comment.image,
    color: colors.primary.main,
    width: "320px",
    height: "320px",
    s: {
      my: 3
    }
  }), updateId === comment.id ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(InputMarkdownTextArea, {
    name: "text",
    s: {
      mb: 3
    },
    value: updateText,
    onChange: setUpdateText
  }), /*#__PURE__*/React.createElement(Button, {
    onClick: handleSubmitUpdate,
    loading: createStatus === StatusStateEnum.LOADING,
    color: "secondary",
    s: {
      px: 8,
      py: 2
    },
    rounded: true,
    display: "inline-block"
  }, t("common.actions.update")), /*#__PURE__*/React.createElement(Button, {
    onClick: handleCancelUpdate,
    color: "transparent",
    s: {
      ml: 3,
      px: 5,
      py: 2
    },
    rounded: true,
    display: "inline-block"
  }, t("common.actions.cancel"))) : /*#__PURE__*/React.createElement(Markdown, {
    text: comment.text
  })), /*#__PURE__*/React.createElement(Grid.Cell, null, /*#__PURE__*/React.createElement(Actions, null, userHasOneOf([AuthoritiesEnum.REQUEST_COMMENTS_WRITE], comment.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown, {
    direction: "bottomLeft",
    handlerProps: {
      rounded: true,
      s: {
        p: 2
      },
      fullWidth: false,
      "data-tooltip-id": "general-tooltip",
      "data-tooltip-content": t("common.actions.options")
    },
    handler: /*#__PURE__*/React.createElement(Icon, {
      src: "dots-vertical",
      color: colors.text.primary,
      display: "block"
    })
  }, _ref2 => {
    let {
      isOpen,
      onItemClick
    } = _ref2;
    return /*#__PURE__*/React.createElement(Dropdown.List, {
      isOpen: isOpen
    }, userHasOneOf([AuthoritiesEnum.REQUEST_COMMENTS_WRITE], comment.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleUpdate(comment.id, comment.text);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "pencil",
      s: {
        mr: 2
      },
      color: "primary"
    }), t("common.actions.edit")), userHasOneOf([AuthoritiesEnum.REQUEST_COMMENTS_WRITE], comment.createdBy.id) && /*#__PURE__*/React.createElement(Dropdown.ListItem, {
      onClick: e => {
        e.stopPropagation();
        handleDelete(comment.id);
        onItemClick();
      }
    }, /*#__PURE__*/React.createElement(Icon, {
      scale: 4.5,
      src: "bin",
      s: {
        mr: 2
      },
      color: "danger"
    }), t("common.actions.delete")));
  }), comment.likes.find(v => v.id === user?.id) ? /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RequestCommentsCard.likes"),
    onClick: () => handleUnlike(comment.id),
    color: "transparent",
    s: {
      px: 2,
      py: 1.3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.danger.main,
    src: "heart"
  }), " ", /*#__PURE__*/React.createElement(Text, {
    color: colors.danger.main,
    fontSize: 3.5,
    weight: "semiBold"
  }, comment.likes.length)) : /*#__PURE__*/React.createElement(Button, {
    "data-tooltip-id": "general-tooltip",
    "data-tooltip-content": t("RequestCommentsCard.likes"),
    onClick: () => handleLike(comment.id),
    color: "transparent",
    s: {
      px: 2,
      py: 1.3
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    color: colors.secondary.main,
    src: "heart"
  }), " ", /*#__PURE__*/React.createElement(Text, {
    color: colors.secondary.main,
    fontSize: 3.5,
    weight: "semiBold"
  }, comment.likes.length)))));
};
export default RequestComment;